import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { getFormattedDate } from 'lib/common';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Button, ContentValue, Tooltip } from 'components/common';
import { Box, BoxRow } from 'components/common/box';

import type {
  CourseQualitySuggestionItem,
  CourseQualitySuggestionSeverity,
  CourseQualitySuggestionStatus
} from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';
import type { UpdateCourseQualityEvaluationReq } from 'shared-types/requests';

interface Props {
  evaluationId: number;
  severity: CourseQualitySuggestionSeverity;
  suggestions: CourseQualitySuggestionItem[];
  updateData: () => Promise<void>;
}

const EditorCourseEvaluationDisplaySuggestions = ({
  evaluationId,
  severity,
  suggestions,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: { userUseName },
    theme
  } = useReduxState();

  const updateStatus = async (
    id: number,
    status: CourseQualitySuggestionStatus
  ) => {
    try {
      await api.put(
        `/admin/courses/${course.id}/quality-evaluation/${evaluationId}/${id}`,
        { status } satisfies UpdateCourseQualityEvaluationReq
      );
      await updateData();
    } catch (error) {
      dispatch.handleErrorDispatch(error);
    }
  };

  const colorBySeverity: Record<CourseQualitySuggestionSeverity, string> = {
    critical: theme.successStandaloneColor,
    recommended: theme.warningStandaloneColor,
    optional: theme.textColorLight
  };

  const iconBySeverity: Record<CourseQualitySuggestionSeverity, IconProp> = {
    critical: 'rocket',
    recommended: 'bolt',
    optional: 'lightbulb'
  };

  const iconProps = {
    icon: iconBySeverity[severity],
    iconStyle: { color: colorBySeverity[severity], marginRight: 10, top: 2 }
  };

  const titleBySeverity: Record<CourseQualitySuggestionSeverity, string> = {
    critical: messages.courseEvaluationSuggestionsCritical,
    recommended: messages.courseEvaluationSuggestionsRecommended,
    optional: messages.courseEvaluationSuggestionsOptional
  };

  const numberOfPendingSuggestions = suggestions.filter(
    (suggestion) => suggestion.status === 'pending'
  ).length;

  return (
    <Box
      padding={0}
      title={titleBySeverity[severity]}
      titleCount={numberOfPendingSuggestions}
    >
      <div className="suggestions">
        {suggestions.map((suggestion) => {
          const isCompleted = suggestion.status === 'completed';
          const isIgnored = suggestion.status === 'ignored';
          const isPending = suggestion.status === 'pending';

          const CompletedWrapper = isCompleted ? Tooltip : 'div';
          const IgnoredWrapper = isIgnored ? Tooltip : 'div';

          const user = suggestion.markedBy
            ? userUseName &&
              suggestion.markedBy.firstName &&
              suggestion.markedBy.lastName
              ? `${suggestion.markedBy.firstName} ${suggestion.markedBy.lastName}`
              : suggestion.markedBy.email || suggestion.markedBy.username
            : messages.activitiesTextUnknownUser;

          const date = suggestion.markedOn
            ? getFormattedDate({ date: suggestion.markedOn })
            : null;

          const tooltipContent =
            isCompleted || isIgnored
              ? getMessageWithPlaceholders({
                  message: isCompleted
                    ? messages.courseEvaluationSuggestionsMarkedCompleted
                    : messages.courseEvaluationSuggestionsMarkedIgnored,
                  placeholders: {
                    user,
                    date
                  }
                })
              : '';

          return (
            <BoxRow key={suggestion.id}>
              <ContentValue style={{ width: '100%' }} {...iconProps}>
                <div className="suggestion">
                  <div className={`label ${!isPending ? 'not-pending' : ''}`}>
                    {suggestion.text}
                  </div>

                  <div className="actions">
                    <CompletedWrapper content={tooltipContent}>
                      <Button
                        category={isCompleted ? 'success' : 'light'}
                        disabled={!isPending && isIgnored}
                        size="tiny"
                        icon="check-circle"
                        iconStyle={{ top: -1 }}
                        onClick={() =>
                          updateStatus(
                            suggestion.id,
                            isCompleted ? 'pending' : 'completed'
                          )
                        }
                        {...(isPending
                          ? { tooltip: messages.commonMarkAsComplete }
                          : {})}
                      />
                    </CompletedWrapper>

                    <IgnoredWrapper content={tooltipContent}>
                      <Button
                        category={isIgnored ? 'success' : 'light'}
                        disabled={!isPending && isCompleted}
                        size="tiny"
                        icon="ban"
                        iconStyle={{ top: -1 }}
                        onClick={() =>
                          updateStatus(
                            suggestion.id,
                            isIgnored ? 'pending' : 'ignored'
                          )
                        }
                        {...(isPending
                          ? { tooltip: messages.commonIgnore }
                          : {})}
                      />
                    </IgnoredWrapper>
                  </div>
                </div>
              </ContentValue>
            </BoxRow>
          );
        })}

        <style jsx>{`
          .suggestions {
            display: flex;
            flex-direction: column;
          }
          .suggestion {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 10px;
            width: 100%;
          }
          .not-pending {
            opacity: 0.5;
          }
          .actions {
            display: flex;
            gap: 5px;
          }
        `}</style>
      </div>
    </Box>
  );
};

export default EditorCourseEvaluationDisplaySuggestions;
